import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Chip,
    Collapse,
    Avatar,
    Paper,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Folder as FolderIcon,
    Timer as TimerIcon,
} from '@mui/icons-material';
import InlineEdit from './Inlineedit.tsx';
import TopicItem from './Topicitem.tsx';
import { Unit, Topic, Week, Subtopic, Day } from '../../../types/curriculum.types.ts';

const UNIT_COLORS = [
    { bg: '#EFF6FF', border: '#BFDBFE', accent: '#3B82F6' },
    { bg: '#F0FDF4', border: '#BBF7D0', accent: '#22C55E' },
    { bg: '#FFF7ED', border: '#FED7AA', accent: '#F59E0B' },
    { bg: '#FDF4FF', border: '#E9D5FF', accent: '#8B5CF6' },
    { bg: '#FFF1F2', border: '#FECDD3', accent: '#EF4444' },
];

interface UnitItemProps {
    unit: Unit;
    unitIndex: number;
    onRenameUnit: (id: string, name: string) => void;
    onDeleteUnit: (id: string) => void;
    onAddTopic: (unitId: string) => void;
    onUpdateTopic: (unitId: string, topicId: string, patch: Partial<Topic>) => void;
    onDeleteTopic: (unitId: string, topicId: string) => void;
    onAddWeek: (unitId: string, topicId: string) => void;
    onUpdateWeek: (unitId: string, topicId: string, weekId: string, patch: Partial<Week>) => void;
    onDeleteWeek: (unitId: string, topicId: string, weekId: string) => void;
    onUpdateWeekTopic: (unitId: string, topicId: string, weekId: string, newTopicId: string) => void;
    onAddSubtopic: (unitId: string, topicId: string, weekId: string) => void;
    onUpdateSubtopic: (unitId: string, topicId: string, weekId: string, subtopicId: string, patch: Partial<Subtopic>) => void;
    onDeleteSubtopic: (unitId: string, topicId: string, weekId: string, subtopicId: string) => void;
    onAddDay: (unitId: string, topicId: string, weekId: string) => void;
    onUpdateDay: (unitId: string, topicId: string, weekId: string, dayId: string, patch: Partial<Day>) => void;
    onDeleteDay: (unitId: string, topicId: string, weekId: string, dayId: string) => void;
}

export default function UnitItem({
    unit,
    unitIndex,
    onRenameUnit,
    onDeleteUnit,
    onAddTopic,
    onUpdateTopic,
    onDeleteTopic,
    onAddWeek,
    onUpdateWeek,
    onDeleteWeek,
    onUpdateWeekTopic,
    onAddSubtopic,
    onUpdateSubtopic,
    onDeleteSubtopic,
    onAddDay,
    onUpdateDay,
    onDeleteDay,
}: UnitItemProps) {
    const [expanded, setExpanded] = useState(true);
    const color = UNIT_COLORS[unitIndex % UNIT_COLORS.length];

    const totalTopics = unit.topics.length;
    const totalWeeks = unit.topics.reduce((sum, t) => sum + t.duration, 0);
    const totalDays = unit.topics.reduce((sum, t) => sum + t.weeks.reduce((s, w) => s + w.days.length, 0), 0);
    const totalHolidays = unit.topics.reduce((sum, t) => sum + t.weeks.reduce((s, w) => s + w.days.filter(d => d.isHoliday).length, 0), 0);
    const totalMarks = unit.topics.reduce((sum, t) => sum + t.weeks.reduce((s, w) => s + w.subtopics.reduce((a, st) => a + (st.marks || 0), 0), 0), 0);

    return (
        <Paper
            elevation={0}
            sx={{
                mb: 2.5,
                border: '2px solid',
                borderColor: color.border,
                borderRadius: 2.5,
                overflow: 'hidden',
                bgcolor: 'white',
                transition: 'all 0.2s ease',
                '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
            }}
        >
            {/* ── Header ── */}
            <Box sx={{ px: 3, py: 1.5, bgcolor: color.bg, borderBottom: `2px solid ${color.border}`, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Avatar sx={{ width: 34, height: 34, bgcolor: color.accent, color: 'white', fontSize: '0.85rem', fontWeight: 700 }}>
                    {unitIndex + 1}
                </Avatar>
                <InlineEdit value={unit.name} onSave={(v) => onRenameUnit(unit.id, v)} variant="h5" bold placeholder="Unit name" fontSize="1.1rem" />
                <Box sx={{ display: 'flex', gap: 0.75, ml: 'auto', flexWrap: 'wrap' }}>
                    <Chip icon={<FolderIcon sx={{ fontSize: 16 }} />} label={`${totalTopics} topics`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: 'white' }} />
                    <Chip icon={<TimerIcon sx={{ fontSize: 16 }} />} label={`${totalWeeks} weeks`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: 'white' }} />
                    <Chip label={`${totalDays} days`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#E0E7FF' }} />
                    {totalHolidays > 0 && (
                        <Chip label={`${totalHolidays} holidays`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#FEE2E2', color: '#991B1B' }} />
                    )}
                    <Chip label={`${totalMarks} marks`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#FEF3C7' }} />
                </Box>
                <Tooltip title={expanded ? 'Collapse' : 'Expand'} arrow>
                    <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0.5 }}>
                        {expanded ? <ExpandLessIcon sx={{ fontSize: 24 }} /> : <ExpandMoreIcon sx={{ fontSize: 24 }} />}
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete Unit" arrow>
                    <IconButton size="small" color="error" onClick={() => onDeleteUnit(unit.id)} sx={{ p: 0.5 }}>
                        <DeleteIcon sx={{ fontSize: 24 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            <Collapse in={expanded}>
                <Box sx={{ p: 2.5 }}>
                    {unit.topics.length === 0 && (
                        <Typography variant="body2" sx={{ color: '#94A3B8', fontSize: '0.9rem', display: 'block', textAlign: 'center', py: 2 }}>
                            No topics. Click "Add Topic" to get started.
                        </Typography>
                    )}

                    {unit.topics.map((topic, ti) => (
                        <TopicItem
                            key={topic.id}
                            topic={topic}
                            topicIndex={ti}
                            onUpdateTopic={(id, patch) => onUpdateTopic(unit.id, id, patch)}
                            onDeleteTopic={(id) => onDeleteTopic(unit.id, id)}
                            onAddWeek={(topicId) => onAddWeek(unit.id, topicId)}
                            onUpdateWeek={(topicId, weekId, patch) => onUpdateWeek(unit.id, topicId, weekId, patch)}
                            onDeleteWeek={(topicId, weekId) => onDeleteWeek(unit.id, topicId, weekId)}
                            onUpdateWeekTopic={(topicId, weekId, newTopicId) => onUpdateWeekTopic(unit.id, topicId, weekId, newTopicId)}
                            onAddSubtopic={(topicId, weekId) => onAddSubtopic(unit.id, topicId, weekId)}
                            onUpdateSubtopic={(topicId, weekId, subtopicId, patch) => onUpdateSubtopic(unit.id, topicId, weekId, subtopicId, patch)}
                            onDeleteSubtopic={(topicId, weekId, subtopicId) => onDeleteSubtopic(unit.id, topicId, weekId, subtopicId)}
                            onAddDay={(topicId, weekId) => onAddDay(unit.id, topicId, weekId)}
                            onUpdateDay={(topicId, weekId, dayId, patch) => onUpdateDay(unit.id, topicId, weekId, dayId, patch)}
                            onDeleteDay={(topicId, weekId, dayId) => onDeleteDay(unit.id, topicId, weekId, dayId)}
                        />
                    ))}

                    <Button
                        fullWidth variant="outlined"
                        startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                        onClick={() => onAddTopic(unit.id)}
                        sx={{
                            mt: 1.5, py: 1.25, borderStyle: 'dashed', fontSize: '0.85rem', textTransform: 'none', borderRadius: 2,
                            borderColor: '#CBD5E1',
                            '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' },
                        }}
                    >
                        Add Topic
                    </Button>
                </Box>
            </Collapse>
        </Paper>
    );
}